import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ReactComponent as BookOpenIcon } from '../../assets/icons/book-open-01.svg';
import { ReactComponent as ApiIcon } from '../../assets/icons/code-browser.svg';
import { ReactComponent as BriefcaseIcon } from '../../assets/icons/briefcase-02.svg';
import { ReactComponent as ActivityIcon } from '../../assets/icons/activity.svg';
import DocumentViewerModal from '../../components/Modal/DocumentViewerModal';
import { getPrivacyPolicy, getTermsOfService, loadDocuments } from '../../utils/documentLoader';
import { getUrl } from 'aws-amplify/storage';
import { STATUS_PAGE_URL } from '../../config/appConfig';
import { PageContainer, PageHeader } from '../../components/AssetBreadCrumb';
import { LinkCardGrid, LinkCardItem } from '../../components/ReusableComponents/LinkCardGrid';
import { modalViewsConst, setModalState } from '../../store/modalSlice';

// The card shape and grid are shared with the Tools page (LinkCardGrid), so the two pages read as one.
const ResourcesIndexPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state: any) => state.user);
  const [userManualExists, setUserManualExists] = useState<boolean>(false);
  const [viewingLegalDoc, setViewingLegalDoc] = useState<'terms' | 'privacy' | null>(null);

  const [docsReady, setDocsReady] = useState(false);

  useEffect(() => {
    loadDocuments().then(() => setDocsReady(true));
  }, []);

  const termsDoc = docsReady ? getTermsOfService() : null;
  const privacyDoc = docsReady ? getPrivacyPolicy() : null;

  // Check if user manual exists in S3
  useEffect(() => {
    const checkUserManualExists = async () => {
      try {
        // Try to get the URL for the user manual
        await getUrl({
          path: 'public/Documentation/UserManual.pdf',
          options: {
            validateObjectExistence: true
          }
        });
        // If we get here without an error, the file exists
        setUserManualExists(true);
      } catch (err) {
        // File doesn't exist or there was an error
        console.log('User manual not available:', err);
        setUserManualExists(false);
      }
    };

    checkUserManualExists();
  }, []);

  const resourceItems: LinkCardItem[] = [
    {
      id: 'api-docs',
      title: 'REST API Documentation',
      description: 'Explore and test the Erode API endpoints with interactive documentation.',
      Icon: ApiIcon,
      cta: 'View resource',
      onSelect: () => navigate('/home/resources/api-docs'),
    },
    // Only offered once the manual is actually in S3
    ...(userManualExists
      ? [{
          id: 'user-guide',
          title: 'User Guide',
          description: 'Learn how to use the Erode platform with step-by-step instructions.',
          Icon: BookOpenIcon,
          cta: 'View resource',
          onSelect: () => navigate('/home/resources/user-guide'),
        } as LinkCardItem]
      : []),
    // Legal agreements — same markdown sources as platform consent
    {
      id: 'terms',
      title: 'Terms of Service',
      description: 'Review the Terms of Service you agreed to when using Erode.',
      Icon: BriefcaseIcon,
      cta: 'View document',
      onSelect: () => setViewingLegalDoc('terms'),
    },
    {
      id: 'privacy',
      title: 'Privacy Policy',
      description: 'Review the Privacy Policy you agreed to when using Erode.',
      Icon: BriefcaseIcon,
      cta: 'View document',
      onSelect: () => setViewingLegalDoc('privacy'),
    },
    {
      id: 'status',
      title: 'System Status',
      description: 'Live uptime and incident history for the Erode platform.',
      Icon: ActivityIcon,
      cta: 'View status page',
      href: STATUS_PAGE_URL,
    },
    {
      id: 'support',
      title: 'Need Help?',
      description: 'Having issues or want to report a bug? Send a report straight to our support team.',
      Icon: BookOpenIcon,
      cta: 'Report an issue',
      // Same report modal the chat uses, instead of a mailto that depends on a configured mail client
      onSelect: () =>
        dispatch(
          setModalState({
            modalOpen: true,
            modalView: modalViewsConst.provideFeedback,
            properties: {
              __modalSize: 'md',
              conversationId: null,
              userEmail: user?.permissionDetails?.email ?? user?.details?.email,
            },
          })
        ),
    },
  ];

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Resources"
        title="Resources"
      />

      <LinkCardGrid items={resourceItems} />

      <DocumentViewerModal
        open={viewingLegalDoc === 'terms'}
        title={termsDoc?.title ?? ''}
        content={termsDoc?.content ?? ''}
        onClose={() => setViewingLegalDoc(null)}
      />
      <DocumentViewerModal
        open={viewingLegalDoc === 'privacy'}
        title={privacyDoc?.title ?? ''}
        content={privacyDoc?.content ?? ''}
        onClose={() => setViewingLegalDoc(null)}
      />
    </PageContainer>
  );
};

export default ResourcesIndexPage;
